import { QueryKeys } from "@/constants/Query";
import { useApi } from "@/hooks/useApi";
import { useApiMutation } from "@/hooks/useApiMutation";
import axiosInstance from "@/lib/axios";
import type { CodeableConcept, Encounter } from "@curai/js-fhir-tools";
import { useQueryClient } from "@tanstack/react-query";

interface AddHospitalizationRequest {
  patientId: string;
  periodStartDate: string;
  reasonCode: Pick<CodeableConcept, "coding" | "text">;
}

export const useAddHospitalization = ({
  patientId,
  onSuccess,
  onError,
}: {
  patientId: string;
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}) => {
  const queryClient = useQueryClient();
  return useApiMutation({
    mutationOptions: {
      mutationFn: (data: AddHospitalizationRequest) =>
        axiosInstance.post("/charting/hospitalization/", data),
      mutationKey:
        QueryKeys.Patient.MedicalRecords.Hospitalization.CREATE(patientId),
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey:
            QueryKeys.Patient.MedicalRecords.Hospitalization.DEFAULT(patientId),
        });
        onSuccess?.();
      },
      onError,
    },
  });
};

interface GetHospitalizationResponse {
  encounters: Encounter[];
}

export const useGetHospitalization = ({ patientId }: { patientId: string }) => {
  return useApi<GetHospitalizationResponse>({
    queryOptions: {
      queryKey:
        QueryKeys.Patient.MedicalRecords.Hospitalization.DEFAULT(patientId),
      queryFn: async () => {
        const response = await axiosInstance.get("/charting/hospitalization/", {
          params: { patientId },
        });
        return response?.data;
      },
    },
  });
};

export const useVoidHospitalization = ({
  hospitalizationId,
  patientId,
  onSuccess,
}: {
  hospitalizationId: string;
  patientId: string;
  onSuccess?: () => void;
}) => {
  const queryClient = useQueryClient();
  return useApiMutation<void, void>({
    mutationOptions: {
      mutationFn: () =>
        axiosInstance.delete(`/charting/hospitalization/${hospitalizationId}`),
      mutationKey: QueryKeys.Patient.MedicalRecords.Hospitalization.VOID(
        patientId,
        hospitalizationId,
      ),
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey:
            QueryKeys.Patient.MedicalRecords.Hospitalization.DEFAULT(patientId),
        });
        onSuccess?.();
      },
    },
  });
};
